import { useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Module-level cache so every component on the page shares one fetch & one value.
let _cache = null;            // last successful payload
let _inflight = null;          // ongoing axios promise (de-dupe across components)
const _subscribers = new Set();

function _broadcast(value) {
  _cache = value;
  _subscribers.forEach((fn) => fn(value));
}

async function _fetchOnce() {
  if (_cache) return _cache;
  if (_inflight) return _inflight;
  _inflight = axios.get(`${API}/settings`)
    .then((res) => {
      _broadcast(res.data || { brochure_url: "" });
      return _cache;
    })
    .catch(() => {
      _broadcast({ brochure_url: "" });
      return _cache;
    })
    .finally(() => { _inflight = null; });
  return _inflight;
}

/**
 * Subscribe to global site settings (currently: brochure_url).
 * Returns the latest known value plus a `refresh()` for admin-side optimistic updates.
 */
export function useSiteSettings() {
  const [settings, setSettings] = useState(_cache || { brochure_url: "" });

  useEffect(() => {
    _subscribers.add(setSettings);
    if (!_cache) _fetchOnce();
    return () => { _subscribers.delete(setSettings); };
  }, []);

  return {
    brochureUrl: settings?.brochure_url || "",
    settings,
    refresh: async () => {
      _cache = null;
      const v = await _fetchOnce();
      return v;
    },
    /**
     * Optimistic update — used by the admin Settings panel after a successful PUT
     * so every other component on the page immediately reflects the new value.
     */
    setLocal: (next) => _broadcast({ ...(_cache || {}), ...next }),
  };
}
